import { TIER_COLORS } from "@eldrun/content";
import { Color, DepthTexture, LessEqualCompare, Matrix4, UnsignedIntType, Vector3, Vector4 } from "three";

/**
 * Estado atmosférico único (doc 05): um valor de fase do dia controla sol, céu, neblina e luz
 * de todos os shaders. Fase 0 = nascer do sol, 0,25 = meio-dia, 0,5 = pôr do sol, 0,75 = meia-noite.
 */
export interface AtmosphereKey {
  phase: number;
  zenith: string;
  horizon: string;
  fog: string;
  light: string;
  intensity: number;
  ambientSky: string;
  ambientGround: string;
}

/**
 * Paleta por hora do dia (doc 05, Direção de arte; pesquisa 11): valores coordenados de baixa
 * saturação, saída direta dos shaders — a dessaturação total de antes (F59) saiu, e o cinza do
 * mundo vem do albedo neutro dos materiais, não de um filtro sobre a luz e o céu. A âncora é o
 * amanhecer/entardecer das referências: abóbada cinza-taupe, faixa larga creme/pêssego no
 * horizonte contínua com a neblina, sol quente contido e preenchimento frio neutro.
 */
export const ATMOSPHERE_KEYS: readonly AtmosphereKey[] = [
  { phase: 0.0, zenith: "#96918B", horizon: "#FFE3C2", fog: "#D8C2AD", light: "#FFE0C4", intensity: 0.85, ambientSky: "#BCC1BF", ambientGround: "#55493E" },
  { phase: 0.08, zenith: "#8A939B", horizon: "#D8DBD3", fog: "#C9CEC7", light: "#F2EDE0", intensity: 1.0, ambientSky: "#ACB4B8", ambientGround: "#56544B" },
  { phase: 0.42, zenith: "#8A939B", horizon: "#D8DBD3", fog: "#C9CEC7", light: "#F2EDE0", intensity: 1.0, ambientSky: "#ACB4B8", ambientGround: "#56544B" },
  { phase: 0.5, zenith: "#96918B", horizon: "#FFE3C2", fog: "#D8C2AD", light: "#FFE0C4", intensity: 0.8, ambientSky: "#BCC1BF", ambientGround: "#55493E" },
  { phase: 0.58, zenith: "#0D111C", horizon: "#202839", fog: "#181F2D", light: "#A3B5D6", intensity: 0.35, ambientSky: "#28334A", ambientGround: "#16181D" },
  { phase: 0.92, zenith: "#0D111C", horizon: "#202839", fog: "#181F2D", light: "#A3B5D6", intensity: 0.35, ambientSky: "#28334A", ambientGround: "#16181D" },
  { phase: 1.0, zenith: "#96918B", horizon: "#FFE3C2", fog: "#D8C2AD", light: "#FFE0C4", intensity: 0.85, ambientSky: "#BCC1BF", ambientGround: "#55493E" },
];

export interface AtmosphereSample {
  zenith: Color;
  horizon: Color;
  fog: Color;
  light: Color;
  intensity: number;
  ambientSky: Color;
  ambientGround: Color;
  /** Direção *para* o sol (ou para a lua à noite), normalizada. */
  lightDirection: Vector3;
  sunDirection: Vector3;
  /** 0 de dia, 1 em plena noite. */
  night: number;
}

const wrap01 = (x: number): number => x - Math.floor(x);

function mixColor(a: string, b: string, t: number, out: Color): Color {
  // Interpolação em espaço linear: as cores da paleta estão em sRGB.
  return out.set(a).lerp(new Color(b), t);
}

/** Amostra a paleta na fase do dia (interpolação suave entre chaves). */
export function sampleAtmosphere(phaseInput: number, out?: AtmosphereSample): AtmosphereSample {
  const phase = wrap01(phaseInput);
  const target = out ?? {
    zenith: new Color(),
    horizon: new Color(),
    fog: new Color(),
    light: new Color(),
    intensity: 1,
    ambientSky: new Color(),
    ambientGround: new Color(),
    lightDirection: new Vector3(),
    sunDirection: new Vector3(),
    night: 0,
  };
  let i = 0;
  while (i < ATMOSPHERE_KEYS.length - 2 && phase > (ATMOSPHERE_KEYS[i + 1] as AtmosphereKey).phase) i++;
  const a = ATMOSPHERE_KEYS[i] as AtmosphereKey;
  const b = ATMOSPHERE_KEYS[i + 1] as AtmosphereKey;
  const raw = (phase - a.phase) / (b.phase - a.phase);
  const t = raw * raw * (3 - 2 * raw);
  mixColor(a.zenith, b.zenith, t, target.zenith);
  mixColor(a.horizon, b.horizon, t, target.horizon);
  mixColor(a.fog, b.fog, t, target.fog);
  mixColor(a.light, b.light, t, target.light);
  mixColor(a.ambientSky, b.ambientSky, t, target.ambientSky);
  mixColor(a.ambientGround, b.ambientGround, t, target.ambientGround);
  target.intensity = a.intensity + (b.intensity - a.intensity) * t;

  // Sol percorre um arco leste → oeste inclinado; à noite a luz vem da lua, oposta.
  const angle = phase * Math.PI * 2;
  target.sunDirection.set(Math.cos(angle), Math.sin(angle), 0.35).normalize();
  const sunHeight = target.sunDirection.y;
  target.night = sunHeight > 0.05 ? 0 : sunHeight < -0.15 ? 1 : (0.05 - sunHeight) / 0.2;
  target.lightDirection.copy(target.night > 0.5 ? target.sunDirection.clone().negate() : target.sunDirection);
  if (target.lightDirection.y < 0.08) target.lightDirection.y = 0.08;
  target.lightDirection.normalize();
  return target;
}

const LUA_CHEIA = { zenith: new Color("#141D33"), horizon: new Color("#2B3C57"), fog: new Color("#1F2B41"), light: new Color("#CFDFF5"), intensity: 0.55 };
const NEBLINA = { day: new Color("#A8B3AE"), night: new Color("#2A3038") };
const gray = new Color();

/** Tira `amount` da saturação da cor (para a luminância). */
function desaturate(c: Color, amount: number): void {
  const l = c.r * 0.299 + c.g * 0.587 + c.b * 0.114;
  c.lerp(gray.setRGB(l, l, l), amount);
}

/**
 * Visual da Mandinga do Dia (doc 05, paletas): *Lua Cheia* clareia a noite com a lua azulada;
 * *Neblina Grossa* dessatura 40 %, puxa a neblina para cinza e escurece a luz 20 %. Devolve o
 * fator da densidade da neblina.
 */
export function applyMandingaLook(sample: AtmosphereSample, mandinga: string, fogMul: number): number {
  if (mandinga === "lua-cheia" && sample.night > 0) {
    const k = sample.night;
    sample.zenith.lerp(LUA_CHEIA.zenith, k);
    sample.horizon.lerp(LUA_CHEIA.horizon, k);
    sample.fog.lerp(LUA_CHEIA.fog, k);
    sample.light.lerp(LUA_CHEIA.light, k);
    sample.intensity += (LUA_CHEIA.intensity - sample.intensity) * k;
    return 1;
  }
  if (mandinga === "neblina-grossa") {
    for (const c of [sample.zenith, sample.horizon, sample.ambientSky]) desaturate(c, 0.4);
    sample.fog.copy(NEBLINA.day).lerp(NEBLINA.night, sample.night);
    sample.intensity *= 0.8;
    return fogMul;
  }
  return 1;
}

/**
 * Estilo do sombreamento toon (F51), lido por `createAtmosphereUniforms`: força do preenchimento frio
 * (`fill`) e a inclinação dele (`fillLift`); brilho do sol na neblina (`sunGlow`, com o expoente
 * `sunGlowPower`); o rim noturno das assombrações (`rimNight`, na cor `rimNightColor`); e as
 * sombras-blob (`blob`: força; `blobStretch`: alongamento para longe da luz; `blobLift`: altura, em
 * m, em que somem; `blobOcclusion`: quanto do ambiente também some na sombra). A rampa (`ramp`): bordas das
 * duas transições, peso da primeira e piso da sombra.
 */
export const TOON_STYLE: ToonStyle = {
  ramp: [0.0, 0.45, 0.35, 0.25],
  fill: 0.25,
  fillLift: 0.35,
  sunGlow: 0.4,
  sunGlowPower: 8,
  rimNight: 0.6,
  rimNightColor: "#8FB2FF",
  blob: 0.6,
  blobStretch: 0.6,
  blobLift: 2.5,
  blobOcclusion: 0.35,
};

export interface ToonStyle {
  ramp: readonly [number, number, number, number];
  fill: number;
  fillLift: number;
  sunGlow: number;
  sunGlowPower: number;
  rimNight: number;
  rimNightColor: string;
  blob: number;
  blobStretch: number;
  blobLift: number;
  blobOcclusion: number;
}

/** Os termos da F51 desligados: o visual de antes (para comparar). */
const STYLE_OFF: Partial<ToonStyle> = { fill: 0, sunGlow: 0, rimNight: 0, blob: 0, blobOcclusion: 0 };

/**
 * Estilo das vitrines (`&estilo=`, só depuração): `0` desliga os termos da F51 (o visual de antes) e
 * `fill:0.3,blob:0.6` troca os números dados (os outros ficam os de `TOON_STYLE`).
 */
export function parseToonStyle(text: string | null): ToonStyle {
  if (text === null || text === "") return TOON_STYLE;
  if (text === "0") return { ...TOON_STYLE, ...STYLE_OFF };
  const out: ToonStyle = { ...TOON_STYLE };
  for (const pair of text.split(",")) {
    const [key, raw] = pair.split(":") as [string, string | undefined];
    const value = Number(raw);
    if (key in out && key !== "ramp" && key !== "rimNightColor" && Number.isFinite(value)) (out as unknown as Record<string, number>)[key] = value;
  }
  return out;
}

/** Escreve o estilo nos uniforms (os mesmos objetos de todos os materiais). */
export function applyToonStyle(uniforms: AtmosphereUniforms, s: ToonStyle): void {
  uniforms.uRamp.value.set(...s.ramp);
  uniforms.uFill.value.set(s.fill, s.fillLift, 0, 0);
  const rim = new Color(s.rimNightColor);
  uniforms.uRimNight.value.set(rim.r, rim.g, rim.b, s.rimNight);
  // Expoente e altura positivos: `pow(x, 0)` e `smoothstep(0, 0, x)` são indefinidos em GLSL.
  uniforms.uSunGlow.value.set(s.sunGlow, Math.max(0.5, s.sunGlowPower), 0, 0);
  uniforms.uBlobStyle.value.set(s.blob, s.blobStretch, Math.max(0.05, s.blobLift), s.blobOcclusion);
}

/**
 * Cor-chave do brilho no Alto (F52): a da pedra-de-raio (`TIER_COLORS[5]`, o metal tingido dos itens
 * e os veios da Pedra Fulminada, a 0,9987), como direção da cor linear, e o cosseno mínimo até ela
 * (o ferro, a cor de tier mais perto, fica a 0,951; o azul-claro do céu, #9CC8F0, a 0,991).
 */
export function glowKey(): Vector4 {
  const c = new Color(TIER_COLORS[5]);
  const v = new Vector3(c.r, c.g, c.b).normalize();
  return new Vector4(v.x, v.y, v.z, 0.998);
}

/**
 * O mapa da sombra quando ela está desligada (Fase 13): um 1 × 1 de profundidade COM comparação. O
 * `sampler2DShadow` precisa de uma textura de profundidade em modo de comparação mesmo sem amostrar
 * (o branch do shader não conta: a validação é no desenho); o vazio do three não garantia o modo, e no
 * Baixo o WebGL recusava todo desenho de terreno, capim, superfície e atores.
 */
let emptyShadow: DepthTexture | undefined;
export function emptyShadowMap(): DepthTexture {
  if (!emptyShadow) {
    emptyShadow = new DepthTexture(1, 1, UnsignedIntType);
    emptyShadow.compareFunction = LessEqualCompare;
    emptyShadow.name = "sombra-vazia";
    // Versão 0 o three nunca sobe (ficaria uma textura sem armazenamento, de novo o erro de formato).
    emptyShadow.needsUpdate = true;
  }
  return emptyShadow;
}

/** Sombras-blob no shader (F51): no máximo 16 projetores por quadro (`uBlobs`). */
export const MAX_BLOBS = 16;

/** Eventos de energia no shader (pesquisa 11): no máximo 8 por quadro (`uEnergy`). */
export const MAX_ENERGY = 8;

/** Uniforms globais compartilhados por todos os materiais (mesmas referências de objeto). */
export function createAtmosphereUniforms() {
  const uniforms = {
    uTime: { value: 0 },
    uLightDir: { value: new Vector3(0.5, 0.8, 0.3).normalize() },
    uSunDir: { value: new Vector3(0.5, 0.8, 0.3).normalize() },
    uLightColor: { value: new Color("#F2EDE0") },
    uAmbientSky: { value: new Color("#ACB4B8") },
    uAmbientGround: { value: new Color("#56544B") },
    uZenith: { value: new Color("#8A939B") },
    uHorizon: { value: new Color("#D8DBD3") },
    uFogColor: { value: new Color("#C9CEC7") },
    uFogDensity: { value: 0.0024 },
    uNight: { value: 0 },
    uSeaLevel: { value: 0 },
    // Estilo (F51): vento único (força, rapidez), rampa, preenchimento, rim noturno e brilho do sol.
    uWind: { value: new Vector4(1, 1, 0, 0) },
    uRamp: { value: new Vector4() },
    uFill: { value: new Vector4() },
    uRimNight: { value: new Vector4() },
    uSunGlow: { value: new Vector4() },
    // Sombras-blob (F51): (x, z, raio, altura dos pés) dos mais perto, preenchidas por `BlobShadows`.
    uBlobs: { value: Array.from({ length: MAX_BLOBS }, () => new Vector4()) },
    uBlobCount: { value: 0 },
    // 0 usa a altura global dos atores; recursos estáticos recebem um corte menor contra penhascos.
    uBlobLift: { value: new Float32Array(MAX_BLOBS) },
    uBlobStyle: { value: new Vector4() },
    // Máscara do contorno no alfa (F51): 0 no canvas (o WebKit mostraria o alfa); 1 só num alvo RGBA que a lê.
    uOutlineMask: { value: 0 },
    // Brilho no Alto (F52): 1 fora do compositor do Alto; lá, a luminância a que os emissivos sobem (o bloom).
    uGlowGain: { value: 1 },
    uGlowKey: { value: glowKey() },
    // Eventos de energia (pesquisa 11): (x, z, raio, força) e a cor de cada um, preenchidos por
    // `EnergyEvents`; um brilho fraco perto de habilidades e impactos, na cor do dono.
    uEnergy: { value: Array.from({ length: MAX_ENERGY }, () => new Vector4()) },
    uEnergyColor: { value: Array.from({ length: MAX_ENERGY }, () => new Color(0, 0, 0)) },
    uEnergyCount: { value: 0 },
    // Pixels do alvo por pixel CSS (o tamanho das partículas), escrito pelo \`PostFx\`.
    uPixelRatio: { value: 1 },
    // O anel do jogador local acende o chão (Fase 13): centro (x, y, z) e raio (m; 0 apaga), e a cor
    // linear já com a força (\`setRingLight\`).
    uRingLight: { value: new Vector4() },
    uRingColor: { value: new Color(0, 0, 0) },
    // Sombra do sol (Fase 13, G3): o mapa, a matriz da luz e os parâmetros (\`SunShadow\`; x: texel em uv,
    // y: desvio na normal em m, z: força, w: ligada). Desligada até o primeiro passe.
    uSunShadowMap: { value: emptyShadowMap() },
    uSunShadowMatrix: { value: new Matrix4() },
    uSunShadowParams: { value: new Vector4(0, 0, 0, 0) },
  };
  applyToonStyle(uniforms, TOON_STYLE);
  return uniforms;
}
export type AtmosphereUniforms = ReturnType<typeof createAtmosphereUniforms>;

/** Raio (m) da faixa acesa do anel do jogador (o meio da faixa de \`createSlotRing\`). */
export const RING_LIGHT_RADIUS = 0.59;

/** Força da luz do anel no chão (multiplica a cor da classe, linear). */
export const RING_LIGHT_GAIN = 1.6;

/** Liga a luz do anel no chão em (x, y, z) na cor `hex` (sRGB); sem cor, apaga. */
export function setRingLight(uniforms: Pick<AtmosphereUniforms, "uRingLight" | "uRingColor">, x: number, y: number, z: number, hex?: string): void {
  if (hex === undefined) {
    uniforms.uRingLight.value.w = 0;
    return;
  }
  uniforms.uRingLight.value.set(x, y, z, RING_LIGHT_RADIUS);
  uniforms.uRingColor.value.set(hex).multiplyScalar(RING_LIGHT_GAIN);
}

export function applyAtmosphere(uniforms: AtmosphereUniforms, sample: AtmosphereSample, time: number): void {
  uniforms.uTime.value = time;
  uniforms.uLightDir.value.copy(sample.lightDirection);
  uniforms.uSunDir.value.copy(sample.sunDirection);
  uniforms.uLightColor.value.copy(sample.light).multiplyScalar(sample.intensity);
  uniforms.uAmbientSky.value.copy(sample.ambientSky);
  uniforms.uAmbientGround.value.copy(sample.ambientGround);
  uniforms.uZenith.value.copy(sample.zenith);
  uniforms.uHorizon.value.copy(sample.horizon);
  uniforms.uFogColor.value.copy(sample.fog);
  uniforms.uNight.value = sample.night;
  // Pesquisa 11: a dessaturação total da F59 saiu daqui. A paleta (`ATMOSPHERE_KEYS`) já é de baixa
  // saturação coordenada; o cinza do mundo vem do albedo neutro (`styleCompose`), e esquentar o céu
  // não recolore os heróis (o acento é composto à parte, por uniforms próprios).
}
